import { useState } from 'react';
import { toast } from 'sonner';
import { apiPost, apiPatch, apiDelete } from '../utils/apiClient';
import { getWeekStartISO } from '../utils/dateUtils';
import { isObjectId, deriveImageUrl, deriveImageThumbUrl } from '../utils/recipeHelpers';
import { hasTime, toMinutes } from '../utils/recipeTime';

// Edit and delete are restricted to the household owner, so a member who reaches either
// endpoint gets a 403. The backend sends the explanation in `error`, which apiClient
// surfaces as err.message; this fallback only covers a 403 that arrives without copy.
const OWNER_ONLY_FALLBACK =
  'Only the household owner can edit or delete recipes. Ask them to make this change for you.';

// Permission copy is a full sentence, so give it longer on screen than a terse failure.
const showRecipeActionError = (e, fallback) => {
  const isPermissionError = e?.status === 403;
  const message = isPermissionError ? (e?.message || OWNER_ONLY_FALLBACK) : (e?.message || fallback);
  toast.error(message, { duration: isPermissionError ? 6000 : 3000 });
};

// Recipe edit / delete / add-to-plan / replace actions and their drawer state, extracted
// from MealPlan.jsx. Behavior-preserving: the handlers are recreated each render (as they
// were inline) so they close over fresh values. Week context and a few MealPlan callbacks
// are passed in as dependencies.
// Week-context deps default to safe stubs so this hook can also be used for edit/delete only
// (e.g. the standalone Recipes route), where add-to-plan is handled by navigating to MealPlan.
export function useRecipeActions({
  weeksData = [],
  setWeeksData = () => {},
  setCurrentWeekIndex = () => {},
  setSelectedDay = () => {},
  actualCurrentWeekIndex = 2,
  weekStartDow = 1,
  getPreferredServes = (_mealType, fallback) => Number(fallback) || 1,
  setViewingMealRecipe = () => {},
  showSuccessBanner = () => {},
  setRecipesRefreshKey = () => {},
  setActiveTab = () => {},
} = {}) {
  const [editingRecipe, setEditingRecipe] = useState(null);
  const [deletingRecipeId, setDeletingRecipeId] = useState(null);
  const [recipeToAddToPlan, setRecipeToAddToPlan] = useState(null);
  const [replaceMealData, setReplaceMealData] = useState(null);

  const handleEditRecipe = (recipe) => {
    setEditingRecipe(recipe);
    setViewingMealRecipe(null);
  };

  // Normalize edited recipe from ManualRecipeForm into backend shape
  const normalizeEditedRecipeForBackend = (r) => {
    // No 'lunch' fallback — meal type is mandatory in ManualRecipeForm, so preserve whatever
    // the user chose rather than silently relabelling a blank value.
    const lowerMealType = String(r.mealType || '').toLowerCase();

    const ingList = Array.isArray(r.ingredients) ? r.ingredients : [];
    const toNumberFromTokens = (tokens) => {
      let total = 0;
      for (const t of tokens) {
        if (/^\d+\/\d+$/.test(t)) {
          const [n, d] = t.split('/').map(Number);
          if (!isNaN(n) && !isNaN(d) && d !== 0) total += n / d;
        } else if (/^\d+(?:\.\d+)?$/.test(t)) {
          const n = Number(t);
          if (!isNaN(n)) total += n;
        } else {
          break;
        }
      }
      return total || null;
    };

    const splitQuantityUnit = (q) => {
      if (typeof q !== 'string') return { quantity: typeof q === 'number' ? q : null, unit: '' };
      const trimmed = q.trim();
      if (!trimmed) return { quantity: null, unit: '' };
      const tokens = trimmed.split(/\s+/);
      const qty = toNumberFromTokens(tokens);
      let consumed = 0;
      for (const t of tokens) {
        if (/^\d+\/\d+$/.test(t) || /^\d+(?:\.\d+)?$/.test(t)) consumed++;
        else break;
      }
      const unit = tokens.slice(consumed).join(' ');
      return { quantity: qty, unit };
    };

    const ingredients = ingList.map((ing) => {
      const { quantity: qRaw, name, category } = ing || {};
      const parts = splitQuantityUnit(typeof qRaw === 'string' ? qRaw : qRaw);
      const qtyValue = parts.quantity;
      return {
        name: String(name || '').trim(),
        // Keep quantity null/empty if we couldn't parse a number to avoid forcing a leading 0
        quantity: qtyValue === null || qtyValue === undefined ? null : qtyValue,
        unit: parts.unit || '',
        category,
      };
    });

    const method = Array.isArray(r.method)
      ? r.method.map((s) => (typeof s === 'string' ? s.trim() : '')).filter(Boolean)
      : [];

    const rawImage = r.photo || r.image || '';
    const image = typeof rawImage === 'string' && rawImage.startsWith('blob:') ? '' : rawImage;
    const imagePath = typeof r.imagePath === 'string' ? r.imagePath : '';
    const rawImageThumb = r.imageThumb || r.thumbnail || r.thumbUrl || '';
    const imageThumb = typeof rawImageThumb === 'string' && rawImageThumb.startsWith('blob:') ? '' : rawImageThumb;
    const imageThumbPath =
      typeof r.imageThumbPath === 'string'
        ? r.imageThumbPath
        : (typeof r.thumbnailPath === 'string' ? r.thumbnailPath : (typeof r.thumbPath === 'string' ? r.thumbPath : ''));

    const rawNutrition = r.nutrition || {};
    const hasAnyNutrition =
      rawNutrition.calories !== undefined ||
      rawNutrition.protein !== undefined ||
      rawNutrition.carbs !== undefined ||
      rawNutrition.fat !== undefined;

    const nutrition = hasAnyNutrition
      ? {
          calories: Number(rawNutrition.calories) || 0,
          protein: Number(rawNutrition.protein) || 0,
          carbs: Number(rawNutrition.carbs) || 0,
          fat: Number(rawNutrition.fat) || 0,
        }
      : undefined;

    return {
      name: r.name || '',
      servings: Number(r.servings) || 1,
      // '' (not 0) when blank, so a missing time stays distinguishable from a real zero
      prepTime: toMinutes(r.prepTime) ?? '',
      cookTime: toMinutes(r.cookTime) ?? '',
      // Dropped only once a COMPLETE breakdown supersedes it. Kept alongside a half
      // breakdown, where it's still the better estimate of the real total.
      totalTime: (hasTime(toMinutes(r.prepTime)) && hasTime(toMinutes(r.cookTime)))
        ? ''
        : (toMinutes(r.totalTime) ?? ''),
      source: r.source || '',
      ingredients,
      method,
      image,
      imagePath,
      imageThumb,
      imageThumbPath,
      tags: Array.isArray(r.tags) ? r.tags : [],
      mealType: lowerMealType,
      nutrition,
    };
  };

  const handleSaveEditedRecipe = (updatedRecipe) => {
    if (!editingRecipe || !editingRecipe.id) {
      setEditingRecipe(null);
      return;
    }

    const body = normalizeEditedRecipeForBackend(updatedRecipe);
    const previousImagePath = editingRecipe?.imagePath || '';
    const previousImageThumbPath = editingRecipe?.imageThumbPath || '';

    apiPatch(`/recipes/${encodeURIComponent(editingRecipe.id)}`, body)
      .then((result) => {
        const forkedRecipe = result?.forked && result?.recipe ? result.recipe : null;
        showSuccessBanner('Recipe updated successfully.');
        // Trigger Recipes to refetch so both the list and any
        // open details can pick up the latest data
        setRecipesRefreshKey((k) => k + 1);
        // Keep weekly-plan detail view in sync
        setViewingMealRecipe((prev) =>
          prev && prev.id === editingRecipe.id
            ? (forkedRecipe
              ? {
                  ...prev,
                  ...updatedRecipe,
                  ...forkedRecipe,
                  id: forkedRecipe._id?.toString?.() || forkedRecipe.id || prev.id,
                  image: forkedRecipe.image || body.image || updatedRecipe.photo || updatedRecipe.image || prev.image,
                  imagePath: forkedRecipe.imagePath || body.imagePath || prev.imagePath,
                  imageThumb: forkedRecipe.imageThumb || body.imageThumb || prev.imageThumb,
                  imageThumbPath: forkedRecipe.imageThumbPath || body.imageThumbPath || prev.imageThumbPath,
                }
              : {
                  ...prev,
                  ...updatedRecipe,
                  image: body.image || updatedRecipe.photo || updatedRecipe.image || prev.image,
                  imagePath: body.imagePath || prev.imagePath,
                  imageThumb: body.imageThumb || prev.imageThumb,
                  imageThumbPath: body.imageThumbPath || prev.imageThumbPath,
                })
            : prev
        );
        // Never clean up the old image on a fork: the backend created a NEW recipe for us and the
        // original (owned by someone else) still points at previousImagePath.
        if (!forkedRecipe && body.imagePath && previousImagePath && body.imagePath !== previousImagePath) {
          const query = new URLSearchParams({ path: previousImagePath });
          if (previousImageThumbPath) query.set('thumbPath', previousImageThumbPath);
          apiDelete(`/uploads/recipe-image?${query.toString()}`)
            .catch((err) => console.warn('Failed to delete old recipe image', err));
        }
      })
      .catch((e) => {
        console.warn('Failed to update recipe', e);
        showRecipeActionError(e, 'Failed to update recipe');
      })
      .finally(() => {
        setEditingRecipe(null);
      });
  };

  const handleDeleteRecipe = (recipeId) => {
    setDeletingRecipeId(recipeId);
    setViewingMealRecipe(null);
  };

  const handleConfirmDeleteRecipe = () => {
    if (!deletingRecipeId) return;

    apiDelete(`/recipes/${encodeURIComponent(deletingRecipeId)}`)
      .then(() => {
        showSuccessBanner('Recipe deleted successfully.');
        // After delete, return user to All Recipes and clear any selection
        setActiveTab('recipes');
        setViewingMealRecipe(null);
        setRecipesRefreshKey((k) => k + 1);
      })
      .catch((e) => {
        console.warn('Failed to delete recipe', e);
        showRecipeActionError(e, 'Failed to delete recipe');
      })
      .finally(() => {
        setDeletingRecipeId(null);
      });
  };

  // Add to meal plan drawer flow
  const handleAddToMealPlan = (recipe) => {
    setRecipeToAddToPlan(recipe);
  };

  const handleConfirmAddToMealPlan = async (weekIndex, day) => {
    if (!recipeToAddToPlan) return;

    const normalizedMealType =
      recipeToAddToPlan.mealType.charAt(0).toUpperCase() + recipeToAddToPlan.mealType.slice(1);

    const existingMeals = weeksData[weekIndex].days[day] || [];
    const existingMeal = existingMeals.find(meal => meal.mealType === normalizedMealType);

    if (existingMeal) {
      setReplaceMealData({
        weekIndex,
        day,
        existingMeal,
        newRecipe: recipeToAddToPlan,
      });
      setRecipeToAddToPlan(null);
      return;
    }

    await addMealToPlanPersisted(weekIndex, day, recipeToAddToPlan, false);
  };

  const getWeekStartISOForIndex = (weekIndex) => {
    const weeksOffset = weekIndex - actualCurrentWeekIndex;
    const base = new Date();
    base.setDate(base.getDate() + weeksOffset * 7);
    return getWeekStartISO(base, weekStartDow);
  };

  const addMealToPlan = (weekIndex, day, recipe, isReplacement = false) => {
    const recipeId = recipe.id;
    const preferredServes = getPreferredServes(recipe.mealType, recipe.servings ?? recipe.serves);
    const newMeal = {
      id: recipeId,
      recipeId: recipeId,
      name: recipe.name,
      image: recipe.imageThumb || recipe.image,
      tags: recipe.tags,
      mealType: recipe.mealType.charAt(0).toUpperCase() + recipe.mealType.slice(1),
      serves: preferredServes,
      isLocalOnly: true, // not yet persisted to weeklyPlans
    };

    setWeeksData(prevWeeks => {
      const newWeeks = [...prevWeeks];
      const existingMeals = newWeeks[weekIndex].days[day] || [];
      let updatedMeals;
      if (isReplacement) {
        const replaced = existingMeals.some(meal => meal.mealType === newMeal.mealType);
        updatedMeals = replaced
          ? existingMeals.map(meal => (meal.mealType === newMeal.mealType ? newMeal : meal))
          : [...existingMeals, newMeal];
      } else {
        updatedMeals = [...existingMeals, newMeal];
      }

      newWeeks[weekIndex] = {
        ...newWeeks[weekIndex],
        days: {
          ...newWeeks[weekIndex].days,
          [day]: updatedMeals,
        },
      };
      return newWeeks;
    });

    setRecipeToAddToPlan(null);
    const action = isReplacement ? 'replaced' : 'added';
    toast.success(`${recipe.name} ${action} to meal plan`, { duration: 3000 });
    setActiveTab('meal-plan');
    setCurrentWeekIndex(weekIndex);
    setSelectedDay(day);
  };

  const regenerateShoppingListForWeek = (iso) =>
    apiPost(`/generate-shopping-list/week-start/${encodeURIComponent(iso)}`);

  const addMealToPlanPersisted = async (weekIndex, dayShort, recipe, isReplacement = false) => {
    const recipeId = String(recipe.id || '');
    const weekStartDate = getWeekStartISOForIndex(weekIndex);
    const dayShortToFull = {
      Sun: 'Sunday',
      Mon: 'Monday',
      Tue: 'Tuesday',
      Wed: 'Wednesday',
      Thu: 'Thursday',
      Fri: 'Friday',
      Sat: 'Saturday',
    };

    // Map day short to assigned date within the selected week. Day columns run in the user's
    // week-start order, and MealPlan re-hydrates a meal onto days[assignedDate - weekStart]
    // (dayShortFromISO). The offset must use that same rotation — otherwise a meal added on,
    // e.g., Monday of a Wednesday-start week is saved under the wrong date and re-places elsewhere.
    const base = new Date(weekStartDate + 'T00:00:00Z');
    const orderedDays = Array.from({ length: 7 }, (_, i) =>
      ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][(weekStartDow + i) % 7]);
    const dayIndex = Math.max(0, orderedDays.indexOf(dayShort));
    base.setUTCDate(base.getUTCDate() + dayIndex);
    const assignedDateISO = base.toISOString().slice(0, 10);
    const assignedDay = dayShortToFull[dayShort] || 'Monday';

    const mealTypeLower = String(recipe.mealType || 'dinner').toLowerCase();
    let assignedMealNumber = 3;
    if (mealTypeLower === 'breakfast') assignedMealNumber = 1;
    else if (mealTypeLower === 'lunch') assignedMealNumber = 2;

    const preferredServes = getPreferredServes(mealTypeLower, recipe.servings ?? recipe.serves);

    // If we have a real recipe id, persist to weeklyPlans; otherwise fall back to local-only add
    if (isObjectId(recipeId)) {
      try {
        const created = await apiPost('/weeklyPlans', {
          recipeId,
          weekStartDate,
          assignedDate: assignedDateISO,
          assignedDay,
          assignedMealNumber,
        });

        const doc = created || {};
        const id = String(doc._id || recipeId);
        const image = deriveImageThumbUrl(doc) || deriveImageThumbUrl(recipe) || deriveImageUrl(doc) || recipe.image;
        const servesFromDoc = Number(doc.serves ?? doc.servings ?? recipe.servings ?? recipe.serves) || 1;
        const tags = Array.isArray(doc.tags) ? doc.tags : (Array.isArray(recipe.tags) ? recipe.tags : []);
        const mealTypeCap =
          typeof doc.mealType === 'string' && doc.mealType.length
            ? doc.mealType.charAt(0).toUpperCase() + doc.mealType.slice(1).toLowerCase()
            : recipe.mealType.charAt(0).toUpperCase() + recipe.mealType.slice(1);

        // If user preference differs from the recipe's serves, patch the backend to scale ingredients now.
        if (preferredServes !== servesFromDoc && isObjectId(id)) {
          try {
            await apiPatch(`/weeklyPlans/${encodeURIComponent(id)}`, { serves: preferredServes });
          } catch (e) {
            console.warn('Failed to apply preferred serves during add:', e);
          }
        }

        const newMeal = {
          id,
          recipeId: doc.recipeId ? String(doc.recipeId) : recipeId,
          name: doc.name || recipe.name,
          image,
          tags,
          mealType: mealTypeCap,
          serves: preferredServes || servesFromDoc,
          isLocalOnly: false,
        };

        setWeeksData((prevWeeks) => {
          const newWeeks = [...prevWeeks];
          if (!newWeeks[weekIndex]) {
            newWeeks[weekIndex] = { label: weeksData[weekIndex]?.label || '', days: { Mon: [], Tue: [], Wed: [], Thu: [], Fri: [], Sat: [], Sun: [] } };
          }
          const existingMeals = newWeeks[weekIndex].days[dayShort] || [];
          let updatedMeals;
          if (isReplacement) {
            const replaced = existingMeals.some((meal) => meal.mealType === newMeal.mealType);
            updatedMeals = replaced
              ? existingMeals.map((meal) => (meal.mealType === newMeal.mealType ? newMeal : meal))
              : [...existingMeals, newMeal];
          } else {
            updatedMeals = [...existingMeals, newMeal];
          }

          newWeeks[weekIndex] = {
            ...newWeeks[weekIndex],
            days: {
              ...newWeeks[weekIndex].days,
              [dayShort]: updatedMeals,
            },
          };
          return newWeeks;
        });

        try {
          await regenerateShoppingListForWeek(weekStartDate);
        } catch (e) {
          console.warn('Failed to regenerate shopping list after add:', e);
        }

        setRecipeToAddToPlan(null);
        const action = isReplacement ? 'replaced' : 'added';
        toast.success(`${recipe.name} ${action} to meal plan`, { duration: 3000 });
        setActiveTab('meal-plan');
        setCurrentWeekIndex(weekIndex);
        setSelectedDay(dayShort);
        return;
      } catch (e) {
        console.warn('Failed to persist weekly plan entry from Add-to-Meal-Plan; falling back to local only:', e);
      }
    }

    // Fallback: local-only add if no valid recipe id or backend failed
    addMealToPlan(weekIndex, dayShort, recipe, isReplacement);
  };

  const handleConfirmReplaceMeal = async () => {
    if (!replaceMealData) return;
    const { weekIndex, day, existingMeal, newRecipe } = replaceMealData;
    const weekStartDate = getWeekStartISOForIndex(weekIndex);

    // Remove existing meal locally
    setWeeksData((prevWeeks) => {
      const newWeeks = [...prevWeeks];
      const existing = newWeeks[weekIndex].days[day] || [];
      newWeeks[weekIndex] = {
        ...newWeeks[weekIndex],
        days: {
          ...newWeeks[weekIndex].days,
          [day]: existing.filter((m) => m.id !== existingMeal.id),
        },
      };
      return newWeeks;
    });

    // If old meal was persisted, delete it server-side
    if (isObjectId(existingMeal.id)) {
      try {
        await apiDelete(`/weeklyPlans/${encodeURIComponent(existingMeal.id)}`);
      } catch (e) {
        console.warn('Failed to delete old weekly plan during replace:', e);
      }
    }

    await addMealToPlanPersisted(weekIndex, day, newRecipe, true);
    setReplaceMealData(null);

    try {
      await regenerateShoppingListForWeek(weekStartDate);
    } catch (e) {
      console.warn('Failed to regenerate shopping list after replace:', e);
    }
  };

  return {
    editingRecipe,
    setEditingRecipe,
    deletingRecipeId,
    setDeletingRecipeId,
    recipeToAddToPlan,
    setRecipeToAddToPlan,
    replaceMealData,
    setReplaceMealData,
    handleEditRecipe,
    handleSaveEditedRecipe,
    handleDeleteRecipe,
    handleConfirmDeleteRecipe,
    handleAddToMealPlan,
    handleConfirmAddToMealPlan,
    addMealToPlanPersisted,
    handleConfirmReplaceMeal,
  };
}
